import { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select items...",
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);

  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onValueChange(newValue);
  };

  const handleClearAll = () => {
    onValueChange([]);
  };

  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder;
    }

    if (value.length === 1) {
      const option = options.find((opt) => opt.value === value[0]);
      return option?.label || value[0];
    }

    const firstOption = options.find((opt) => opt.value === value[0]);
    const firstLabel = firstOption?.label || value[0];
    return `${firstLabel} + ${value.length - 1}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* `asChild` so the trigger *is* the Button. Without it Radix renders its
          own button around this one, which is invalid HTML and makes the inner
          button unreachable by keyboard. */}
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between min-w-[200px] h-9 border border-input bg-background text-foreground font-normal shadow-xs hover:bg-accent hover:text-accent-foreground",
            className
          )}
        >
          {/* The trigger holds its width whatever is selected, so the row of
              filters around it does not reflow on every pick; the value it
              cannot fit stays available on hover. */}
          <span className="truncate" title={getDisplayText()}>
            {getDisplayText()}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* The list is as wide as its options need, not as wide as the trigger.
          Agent names and dataset titles routinely outrun a filter-sized
          trigger, and pinning the panel to it wrapped each one onto three
          lines. The panel floats, so growing rightward costs no layout — but
          it stops at a readable measure and inside the viewport, and a label
          past that truncates rather than reflowing the whole list. */}
      <PopoverContent
        className="w-auto max-w-[min(32rem,calc(100vw-2rem))] min-w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="max-h-64 overflow-auto">
          {value.length > 0 && (
            <div className="px-3 py-2 border-b">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="mr-1 h-3 w-3" />
                Clear all
              </Button>
            </div>
          )}

          <div className="p-1">
            {options.map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                onClick={() => handleToggleOption(option.value)}
              >
                <Checkbox
                  checked={value.includes(option.value)}
                  onChange={() => handleToggleOption(option.value)}
                  className="h-4 w-4"
                />
                <span className="min-w-0 flex-1 truncate" title={option.label}>
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelect;
